// src/components/Layouts/ProgramLayout.js

import React, { useEffect, useState } from 'react';
import { NavLink, Outlet, useParams, useNavigate } from 'react-router-dom';
import styles from './ProgramLayout.module.css';
import { FaCalendarAlt, FaUsers, FaTasks, FaCogs, FaTrophy, FaBars, FaTimes } from 'react-icons/fa';
import axios from 'axios';

// Cloudinary hosted logo
const logo = 'https://res.cloudinary.com/dqushvncg/image/upload/v1765434267/logo_zvkjeg.png';

const ProgramLayout = () => {
  const { programId } = useParams(); // Retrieve programId from URL
  const [program, setProgram] = useState(null);
  const [club, setClub] = useState(null); // New state for club details
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(false);
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false);

  const navigate = useNavigate(); // For navigation on click

  // Redirect to events page by default when landing on program route
  useEffect(() => {
    const currentPath = window.location.pathname;
    if (currentPath === `/program/${programId}`) {
      navigate(`/program/${programId}/events`, { replace: true });
    }
  }, [programId, navigate]);

  // Fetch program and club details
  useEffect(() => {
    const fetchProgramAndClubDetails = async () => {
      try {
        // Fetch program details
        const programResponse = await axios.get(
          `${process.env.REACT_APP_BACKEND_URL}/api/programs/${programId}`
        );
        const fetchedProgram = programResponse.data;
        setProgram(fetchedProgram);

        // Extract clubId correctly (handle both string and object cases)
        const clubId =
          fetchedProgram.clubId && typeof fetchedProgram.clubId === 'object'
            ? fetchedProgram.clubId._id
            : fetchedProgram.clubId;

        // Fetch club details if clubId exists
        if (clubId) {
          try {
            const clubResponse = await axios.get(
              `${process.env.REACT_APP_BACKEND_URL}/api/clubs/${clubId}`
            );
            setClub(clubResponse.data);
          } catch (clubErr) {
            console.error('Error fetching club details:', clubErr);
            // Continue without club details
          }
        }

        setLoading(false);
      } catch (err) {
        console.error('Error fetching details:', err);
        setError(true);
        setLoading(false);
      }
    };

    if (programId) {
      fetchProgramAndClubDetails();
    } else {
      setError(true);
      setLoading(false);
    }
  }, [programId]);

  if (loading) {
    return <div className={styles.message}>Loading program details...</div>;
  }

  if (error || !program) {
    return <div className={styles.message}>Failed to load program details.</div>;
  }

  return (
    <div className={styles.layout}>
      {/* Combined Banner with Names and Navigation */}
      <div className={styles.banner}>
        {program.bannerImageUrl && (
          <img
            src={program.bannerImageUrl}
            alt={`${program.name} Banner`}
            className={styles.bannerImage}
          />
        )}
        <div className={styles.bannerContent}>
          <div className={styles.programHeader}>
            {club && club.clubLogo && (
              <img
                src={club.clubLogo}
                alt={club.name + ' logo'}
                className={styles.clubLogo}
              />
            )}
            <div className={styles.programInfo}>
              {club && (
                <span
                  className={styles.clubName}
                  style={{ cursor: 'pointer', textDecoration: 'underline' }}
                  onClick={() => club._id && navigate(`/clubs/${club._id}`)}
                  title="Go to Club Details"
                >
                  {club.name}
                </span>
        )}
        <h1 className={styles.programName}>{program.name}</h1>
      </div>
          </div>

          {/* Hamburger menu icon for mobile */}
          <button
            className={styles.hamburgerBtn}
            onClick={() => setMobileMenuOpen((open) => !open)}
            aria-label={mobileMenuOpen ? 'Close menu' : 'Open menu'}
          >
            {mobileMenuOpen ? <FaTimes /> : <FaBars />}
          </button>

          {/* Navigation in the same row (desktop) or dropdown (mobile) */}
      <nav className={styles.menuBar}>
            <ul
              className={
                mobileMenuOpen
                  ? `${styles.navList} ${styles.navListOpen}`
                  : styles.navList
              }
            >
          <li className={styles.navItem}>
            <NavLink
              to={`/program/${programId}/events`}
              className={({ isActive }) =>
                isActive ? `${styles.navLink} ${styles.activeLink}` : styles.navLink
              }
                  onClick={() => setMobileMenuOpen(false)}
            >
              <FaCalendarAlt className={styles.icon} /> Events
            </NavLink>
          </li>
          <li className={styles.navItem}>
            <NavLink
              to={`/program/${programId}/teams`}
              className={({ isActive }) =>
                isActive ? `${styles.navLink} ${styles.activeLink}` : styles.navLink
              }
                  onClick={() => setMobileMenuOpen(false)}
            >
              <FaUsers className={styles.icon} /> Teams
            </NavLink>
          </li>
          <li className={styles.navItem}>
            <NavLink
              to={`/program/${programId}/team-items`}
              className={({ isActive }) =>
                isActive ? `${styles.navLink} ${styles.activeLink}` : styles.navLink
              }
                  onClick={() => setMobileMenuOpen(false)}
            >
              <FaTasks className={styles.icon} /> Assignments
            </NavLink>
          </li>
          <li className={styles.navItem}>
            <NavLink
              to={`/program/${programId}/equipment`}
              className={({ isActive }) =>
                isActive ? `${styles.navLink} ${styles.activeLink}` : styles.navLink
              }
                  onClick={() => setMobileMenuOpen(false)}
            >
              <FaCogs className={styles.icon} /> Equipment
            </NavLink>
          </li>
          <li className={styles.navItem}>
            <NavLink
              to={`/program/${programId}/awards`}
              className={({ isActive }) =>
                isActive ? `${styles.navLink} ${styles.activeLink}` : styles.navLink
              }
                  onClick={() => setMobileMenuOpen(false)}
            >
              <FaTrophy className={styles.icon} /> Awards
            </NavLink>
          </li>
        </ul>
      </nav>
        </div>
      </div>
      <main className={styles.mainContent}>
        <Outlet />
      </main>
    </div>
  );
};

export default ProgramLayout;
